import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { displayOrDash } from '../../data/recordRoom';
import { useAssignmentOfficerOptions } from '../../hooks/useAssignmentOfficerOptions';
import { useCategoryAllocationCounselOptions } from '../../hooks/useCategoryAllocationCounselOptions';
import { formatCategoryAllocationUserLabel } from '../../utils/configureAssignmentFields';
import ConfirmDialog from '../ui/ConfirmDialog';
import OverlayModal from '../ui/OverlayModal';
import WizardSelect from '../ui/WizardSelect';

export type BulkReassignSaveDetails = {
  reallocateNow: boolean;
  assignedUserId: string;
  supervisorId: string;
  stateAttorneyId: string;
};

export type BulkReassignModalProps = {
  isOpen: boolean;
  idPrefix: string;
  selectedCaseCount: number;
  fileCategoryId: string;
  subFileCategoryId: string;
  fileCategoryName: string;
  subFileCategoryName: string;
  saving: boolean;
  onClose: () => void;
  onCancel: () => void;
  onSave: (details: BulkReassignSaveDetails) => Promise<boolean>;
};

function optionLabel(options: { id: string; label: string }[], id: string): string {
  if (!id) return displayOrDash('');
  return options.find((option) => option.id === id)?.label ?? displayOrDash('');
}

export default function BulkReassignModal({
  isOpen,
  idPrefix,
  selectedCaseCount,
  fileCategoryId,
  subFileCategoryId,
  fileCategoryName,
  subFileCategoryName,
  saving,
  onClose,
  onCancel,
  onSave,
}: BulkReassignModalProps) {
  const { t } = useTranslation();

  const [reallocateNow, setReallocateNow] = useState(true);
  const [selectedSupervisorId, setSelectedSupervisorId] = useState('');
  const [selectedStateAttorneyId, setSelectedStateAttorneyId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState<BulkReassignSaveDetails | null>(null);

  const {
    loadState: officersLoadState,
    supervisorOptions,
    stateAttorneyOptions,
  } = useAssignmentOfficerOptions(true);

  const {
    options: counselSelectOptions,
    loadState: counselLoadState,
    selectedCounselId,
    setSelectedCounselId,
    reset: resetCounselOptions,
    resolveSelectedCounsel,
  } = useCategoryAllocationCounselOptions({
    active: isOpen && reallocateNow,
    fileCategoryId,
    subFileCategoryId,
    fileCategoryName,
    subFileCategoryName,
  });

  const fieldsDisabled = !reallocateNow || saving;

  const validate = (): boolean => {
    if (!reallocateNow) {
      setErrors({});
      return true;
    }

    const next: Record<string, string> = {};
    if (!selectedCounselId) {
      next.selectedCounselId = t('tasks.assign.counselRequired');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    if (!reallocateNow) {
      await onSave({
        reallocateNow: false,
        assignedUserId: '',
        supervisorId: '',
        stateAttorneyId: '',
      });
      return;
    }

    const counsel = resolveSelectedCounsel();
    if (!counsel) return;

    setPendingSave({
      reallocateNow: true,
      assignedUserId: counsel.userId,
      supervisorId: selectedSupervisorId,
      stateAttorneyId: selectedStateAttorneyId,
    });
    setSaveConfirmOpen(true);
  };

  const confirmSave = async () => {
    if (!pendingSave) return;
    const saved = await onSave(pendingSave);
    if (!saved) return;
    setSaveConfirmOpen(false);
    setPendingSave(null);
  };

  const saveConfirmMessage = useMemo(() => {
    const counsel = resolveSelectedCounsel();
    const counselName = counsel ? formatCategoryAllocationUserLabel(counsel) : displayOrDash('');
    const supervisorName = optionLabel(supervisorOptions, selectedSupervisorId);
    const stateAttorneyName = optionLabel(stateAttorneyOptions, selectedStateAttorneyId);

    return [
      t('tasks.assign.reassignNowConfirmIntro', { count: selectedCaseCount }),
      t('tasks.assign.reassignNowConfirmCounselLine', { name: counselName }),
      t('tasks.assign.reassignNowConfirmSupervisorLine', { name: supervisorName }),
      t('tasks.assign.reassignNowConfirmStateAttorneyLine', { name: stateAttorneyName }),
      '',
      t('tasks.assign.reassignNowConfirmProceed', { count: selectedCaseCount }),
    ].join('\n');
  }, [
    resolveSelectedCounsel,
    selectedCaseCount,
    selectedStateAttorneyId,
    selectedSupervisorId,
    stateAttorneyOptions,
    supervisorOptions,
    t,
  ]);

  const canSave = !saving && (reallocateNow ? Boolean(selectedCounselId) : true);

  return (
    <>
    <OverlayModal
      isOpen={isOpen}
      overlayClassName="overlay"
      contentClassName="cn-mbox cfg-assignment-modal"
      centerVertically
      onClose={onClose}
    >
      <div className="cn-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span>{t('tasks.assign.configureAssignment')}</span>
        <button type="button" className="cn-x" aria-label={t('common.close')} onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="cn-body">
        <div className="wfa-radio-row" style={{ marginBottom: 16 }}>
          <label>
            <input
              type="radio"
              name={`${idPrefix}-reassign-timing`}
              checked={reallocateNow}
              onChange={() => {
                setReallocateNow(true);
                setErrors({});
              }}
              disabled={saving}
            />
            <span className="wfa-radio-label">{t('tasks.assign.reassignNow')}</span>
          </label>
          <label>
            <input
              type="radio"
              name={`${idPrefix}-reassign-timing`}
              checked={!reallocateNow}
              onChange={() => {
                setReallocateNow(false);
                setErrors({});
                resetCounselOptions();
              }}
              disabled={saving}
            />
            <span className="wfa-radio-label">{t('tasks.assign.reassignLater')}</span>
          </label>
        </div>

        {reallocateNow ? (
          <div
            className="cfg-reassign-info cfg-reassign-info--now"
            role="note"
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              padding: '10px 12px',
              marginBottom: 16,
              borderRadius: 'var(--rs)',
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              fontSize: 13,
              color: 'var(--text)',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{t('tasks.assign.reassignNowInfo')}</span>
          </div>
        ) : (
          <div
            className="cfg-reassign-info cfg-reassign-info--later"
            role="note"
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              padding: '10px 12px',
              marginBottom: 16,
              borderRadius: 'var(--rs)',
              background: '#fffbeb',
              border: '1px solid #fde68a',
              fontSize: 13,
              color: 'var(--text)',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>{t('tasks.assign.reassignLaterInfo')}</span>
          </div>
        )}

        <div className="cfg-assignment-form">
          <div className="ccr-fg">
            <label>
              {t('tasks.assign.counsel')} {reallocateNow ? <span className="req">*</span> : null}
            </label>
            <WizardSelect
              id={`${idPrefix}-counsel`}
              value={selectedCounselId}
              placeholder={
                counselLoadState === 'error'
                  ? t('common.failedToLoad')
                  : t('tasks.assign.counsel')
              }
              options={counselSelectOptions}
              disabled={fieldsDisabled || counselLoadState === 'error'}
              loading={counselLoadState === 'loading'}
              hasError={Boolean(errors.selectedCounselId)}
              emptyMessage={t('tasks.assign.noUsersFound')}
              searchable
              onChange={(counselId) => {
                setSelectedCounselId(counselId);
                setErrors((e) => ({ ...e, selectedCounselId: '' }));
              }}
            />
            {errors.selectedCounselId ? (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
                {errors.selectedCounselId}
              </div>
            ) : null}
          </div>

          <div className="ccr-fg">
            <label>{t('admin.shared.supervisor')}</label>
            <WizardSelect
              id={`${idPrefix}-supervisor`}
              value={selectedSupervisorId}
              placeholder={
                officersLoadState === 'error' ? t('common.failedToLoad') : t('admin.userCategories.selectSupervisor')
              }
              options={supervisorOptions}
              disabled={fieldsDisabled || officersLoadState === 'error'}
              loading={officersLoadState === 'loading'}
              emptyMessage={t('common.noDataFound')}
              searchable
              onChange={setSelectedSupervisorId}
            />
          </div>

          <div className="ccr-fg">
            <label>{t('tasks.assign.stateAttorney')}</label>
            <WizardSelect
              id={`${idPrefix}-state-attorney`}
              value={selectedStateAttorneyId}
              placeholder={
                officersLoadState === 'error' ? t('common.failedToLoad') : t('admin.userCategories.selectStateAttorney')
              }
              options={stateAttorneyOptions}
              disabled={fieldsDisabled || officersLoadState === 'error'}
              loading={officersLoadState === 'loading'}
              emptyMessage={t('common.noDataFound')}
              searchable
              onChange={setSelectedStateAttorneyId}
            />
          </div>
        </div>
      </div>
      <div className="cn-footer">
        <button type="button" className="mfb" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button type="button" className="mfb mfb-dk" onClick={() => void handleSave()} disabled={!canSave}>
          {saving ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </OverlayModal>

    <ConfirmDialog
      isOpen={saveConfirmOpen}
      title={t('tasks.assign.reassignNowConfirmTitle')}
      message={saveConfirmMessage}
      multiline
      confirmDisabled={saving}
      onClose={() => {
        if (saving) return;
        setSaveConfirmOpen(false);
        setPendingSave(null);
      }}
      onConfirm={() => void confirmSave()}
    />
    </>
  );
}
